import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

// ✅ Use same API_BASE pattern as Login.js for production compatibility
const API_BASE = process.env.REACT_APP_API_BASE || '/api';

const containerStyle = { maxWidth: 1200, margin: '40px auto', padding: 24, border: '1px solid #ddd', borderRadius: 10, background: '#fafafa' };
const inputStyle = { width: '100%', padding: 10, marginBottom: 8, boxSizing: 'border-box', border: '1px solid #ccc', borderRadius: 6, fontSize: 14 };
const labelStyle = { display: 'block', marginBottom: 4, fontWeight: 500, fontSize: 14 };
const btnPrimary = { padding: '10px 16px', background: '#28a745', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', marginRight: 8 };
const btnSecondary = { padding: '10px 16px', background: '#6c757d', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', marginRight: 8 };
const btnDanger = { padding: '6px 12px', background: '#dc3545', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer' };
const btnAdd = { padding: '6px 12px', background: '#007BFF', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', width: '100%' };

const collectTags = (q) => {
  const tags = [];
  const addTag = (tag) => { if (tag && tag.trim() !== '') tags.push(tag.trim()); };
  addTag(q.question_tag_1); addTag(q.question_tag_2); addTag(q.question_tag_3);
  ['a', 'b', 'c', 'd'].forEach(opt => {
    addTag(q[`answer_${opt}_tag1`]); addTag(q[`answer_${opt}_tag2`]); addTag(q[`answer_${opt}_tag3`]);
  });
  return [...new Set(tags)];
};

export default function ExamBuilder() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editId = searchParams.get('edit');
  
  const [step, setStep] = useState(1);
  const [examInfo, setExamInfo] = useState({ exam_name: '', exam_topic: 'math' });
  
  // ✅ Filter States - all start as empty string
  const [levelFilter, setLevelFilter] = useState('');
  const [bandFilter, setBandFilter] = useState('');
  const [tagFilter, setTagFilter] = useState('');
  const [rawQuestions, setRawQuestions] = useState([]);
  const [uniqueTags, setUniqueTags] = useState([]);
  const [uniqueBands, setUniqueBands] = useState([]);

  const [flowNodes, setFlowNodes] = useState([]);
  const [isEditMode, setIsEditMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // ✅ Load existing exam if editing
  useEffect(() => {
    if (editId) {
      setIsEditMode(true);
      setLoading(true);
      // ✅ Refactored: Use API_BASE + relative path
      fetch(`${API_BASE}/admin/exams/${editId}/questions`)
        .then(res => { if (!res.ok) throw new Error('Failed to load exam'); return res.json(); })
        .then(data => {
          setExamInfo({ exam_name: data.exam.exam_name, exam_topic: data.exam.exam_topic });
          const nodes = data.questions.map(q => ({
            tempId: q.flow_id,
            source_question_id: q.id,
            question_text: q.question_text,
            question_details: q,
            next_correct_id: q.next_correct_flow_id || null,
            next_wrong_id: q.next_wrong_flow_id || null,
            is_start: q.is_start
          }));
          setFlowNodes(nodes);
          setStep(2);
        })
        .catch(err => setError(err.message))
        .finally(() => setLoading(false));
    }
  }, [editId]);

  // ✅ Fetch questions & extract unique filters
  useEffect(() => {
    if (step === 2 && examInfo.exam_topic) {
      // ✅ Refactored: Use API_BASE + relative path
      fetch(`${API_BASE}/admin/questions/${examInfo.exam_topic}`)
        .then(res => res.json())
        .then(data => {
          console.log('📥 Fetched questions:', data.length);
          setRawQuestions(data);
          
          // Extract unique tags
          const tags = new Set();
          const bands = new Set();
          data.forEach(q => {
            [q.question_tag_1, q.question_tag_2, q.question_tag_3].forEach(t => t && tags.add(t));
            if (examInfo.exam_topic === 'english') {
              if(q.band1) bands.add(q.band1);
              if(q.band2) bands.add(q.band2);
            }
          });
          setUniqueTags(Array.from(tags).sort());
          setUniqueBands(Array.from(bands).sort());
        })
        .catch(() => setError('Failed to load questions'));
    }
  }, [step, examInfo.exam_topic]);

  // ✅ Apply combined filters (Level + Band + Tag) with debug logging
  const filteredQuestions = useMemo(() => {
    const result = rawQuestions.filter(q => {
      if (levelFilter && q.level !== levelFilter) return false;
      if (bandFilter && examInfo.exam_topic === 'english') {
        if (q.band1 !== bandFilter && q.band2 !== bandFilter) return false;
      }
      if (tagFilter) {
        const qTags = [q.question_tag_1, q.question_tag_2, q.question_tag_3].filter(Boolean);
        if (!qTags.includes(tagFilter)) return false;
      }
      return true;
    });
    
    console.log('🔍 Filtered questions:', { 
      total: rawQuestions.length, 
      afterFilter: result.length,
      filters: { levelFilter, bandFilter, tagFilter }
    });
    
    return result;
  }, [rawQuestions, levelFilter, bandFilter, tagFilter, examInfo.exam_topic]);

  const handleAddNode = (question) => {
    const newNode = {
      tempId: Date.now(),
      source_question_id: question.id,
      question_text: question.question_text,
      question_details: question,
      next_correct_id: null,
      next_wrong_id: null,
      is_start: flowNodes.length === 0
    };
    setFlowNodes([...flowNodes, newNode]);
  };

  const updateNode = (tempId, field, value) => {
    setFlowNodes(prev => prev.map(node => {
      if (node.tempId === tempId) {
        return { ...node, [field]: value === 'null' ? null : Number(value) };
      }
      return node;
    }));
  };

  const removeNode = (tempId) => {
    const isStart = flowNodes.find(n => n.tempId === tempId)?.is_start;
    const filtered = flowNodes.filter(n => n.tempId !== tempId);
    if (isStart && filtered.length > 0) filtered[0].is_start = true;
    setFlowNodes(filtered);
  };

  const clearFilters = () => {
    setLevelFilter('');
    setBandFilter('');
    setTagFilter('');
  };

  const handleSave = async () => {
    if (!examInfo.exam_name.trim()) { setError('Exam name is required'); return; }
    if (flowNodes.length === 0) { setError('Add at least one question'); return; }
    if (!flowNodes.some(n => n.is_start)) { setError('You must select a "Start Question"'); return; }

    // ✅ Check for duplicates
    const sourceIds = flowNodes.map(n => n.source_question_id);
    if (new Set(sourceIds).size !== sourceIds.length) {
      setError('❌ Each question can only be used once in this exam. Please remove duplicates.');
      return;
    }

    setLoading(true); setError('');
    try {
      const getTargetSourceId = (targetTempId) => {
        if (!targetTempId) return null;
        const target = flowNodes.find(n => n.tempId === Number(targetTempId));
        return target?.source_question_id ?? null;
      };

      const payload = {
        ...examInfo,
        flow: flowNodes.map(node => ({
          source_question_id: node.source_question_id,
          next_correct_source_id: getTargetSourceId(node.next_correct_id),
          next_wrong_source_id: getTargetSourceId(node.next_wrong_id),
          is_start: node.is_start
        }))
      };

      // ✅ Refactored: Use API_BASE + relative path for both POST and PUT
      const url = isEditMode 
        ? `${API_BASE}/admin/exams/${editId}` 
        : `${API_BASE}/admin/exams`;
      const method = isEditMode ? 'PUT' : 'POST';

      const res = await fetch(url, { 
        method, 
        headers: { 'Content-Type': 'application/json' },
        // ✅ Add credentials if your backend uses cookies/sessions
        credentials: 'include',
        body: JSON.stringify(payload) 
      });
      
      if (!res.ok) throw new Error((await res.json()).error || 'Failed to save');
      alert(`✅ Exam ${isEditMode ? 'updated' : 'created'} successfully!`);
      navigate('/admin/view-exams');
    } catch (err) { setError(err.message); } finally { setLoading(false); }
  };

  const renderQuestionPreview = (q) => {
    const isEnglish = examInfo.exam_topic === 'english';
    const levelDisplay = q.level ? `Lvl ${q.level}` : 'Lvl —';
    const context = isEnglish ? `Band: ${q.band1 || '—'} / ${q.band2 || '—'}` : `Chapter: ${q.chapter || '—'}`;
    const questionTags = [q.question_tag_1, q.question_tag_2, q.question_tag_3].filter(t => t && t.trim() !== '').map(t => t.trim());
    const answerTags = [];
    ['a', 'b', 'c', 'd'].forEach(opt => {
      [1, 2, 3].forEach(i => {
        const tag = q[`answer_${opt}_tag${i}`];
        if (tag && tag.trim() !== '') answerTags.push(tag.trim());
      });
    });
    const uniqueAnswerTags = [...new Set(answerTags)];

    return (
      <div style={{ fontSize: 12, lineHeight: 1.4 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
          <span style={{ fontWeight: 700, color: '#fff', background: '#6c757d', padding: '2px 8px', borderRadius: 4, fontSize: 10, fontFamily: 'monospace' }}>Q-ID: {q.id}</span>
          <span style={{ fontWeight: 600, color: '#28a745' }}>{levelDisplay}</span>
          <span style={{ color: '#666', fontSize: 11 }}>{context}</span>
        </div>
        <div style={{ fontWeight: 500, marginBottom: 4, fontSize: 13 }}>{q.question_text.substring(0, 55)}{q.question_text.length > 55 ? '...' : ''}</div>
        {questionTags.length > 0 && <div style={{ color: '#6f42c1', fontSize: 10 }}><strong>Q:</strong> {questionTags.slice(0, 3).join(', ')}{questionTags.length > 3 ? '...' : ''}</div>}
        {uniqueAnswerTags.length > 0 && <div style={{ color: '#007BFF', fontSize: 10 }}><strong>A:</strong> {uniqueAnswerTags.slice(0, 3).join(', ')}{uniqueAnswerTags.length > 3 ? '...' : ''}</div>}
      </div>
    );
  };

  if (loading && isEditMode) return <div style={{ textAlign: 'center', marginTop: 60 }}>Loading exam...</div>;

  return (
    <div style={containerStyle}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h2 style={{ margin: 0 }}>{isEditMode ? 'Edit Exam' : 'Exam Builder'}</h2>
        <button onClick={() => navigate('/admin/view-exams')} style={btnSecondary}>← Back</button>
      </div>
      {error && <div style={{ padding: 10, background: '#f8d7da', color: '#721c24', borderRadius: 6, marginBottom: 15 }}>{error}</div>}

      {step === 1 && (
        <div style={{ background: '#fff', padding: 20, borderRadius: 8, border: '1px solid #ddd' }}>
          <h3>1. Exam Details</h3>
          <label style={labelStyle}>Exam Name:
            <input style={inputStyle} value={examInfo.exam_name} onChange={e => setExamInfo({...examInfo, exam_name: e.target.value})} placeholder="e.g. Level 3 Math Diagnostic" />
          </label>
          <label style={labelStyle}>Exam Topic:
            <select style={inputStyle} value={examInfo.exam_topic} onChange={e => setExamInfo({...examInfo, exam_topic: e.target.value})} disabled={isEditMode}>
              <option value="english">English</option><option value="math">Math</option><option value="science">Science</option>
            </select>
            {isEditMode && <small style={{ color: '#856404', display: 'block', marginTop: 4 }}>⚠️ Topic cannot be changed while editing.</small>}
          </label>
          <div style={{ marginTop: 20 }}><button onClick={() => setStep(2)} style={btnPrimary}>Next: Build Flow →</button></div>
        </div>
      )}

      {step === 2 && (
        <div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
            <div style={{ background: '#fff', padding: 15, borderRadius: 8, border: '1px solid #ddd' }}>
              <div style={{ marginBottom: 10 }}>
                <h3 style={{ margin: '0 0 8px 0' }}>Available Questions</h3>
                {/* ✅ Advanced Filter Bar */}
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 10 }}>
                  <span style={{ fontWeight: 600, fontSize: 13 }}>Filters:</span>
                  <select value={levelFilter} onChange={e => setLevelFilter(e.target.value)} style={{ ...inputStyle, width: 'auto', marginBottom: 0, fontSize: 12, padding: '4px 8px' }}>
                    <option value="">All Levels</option>
                    {['1','2','3','4','5','6'].map(l => <option key={l} value={l}>Lvl {l}</option>)}
                  </select>
                  {examInfo.exam_topic === 'english' && (
                    <select value={bandFilter} onChange={e => setBandFilter(e.target.value)} style={{ ...inputStyle, width: 'auto', marginBottom: 0, fontSize: 12, padding: '4px 8px' }}>
                      <option value="">All Bands</option>
                      {uniqueBands.map(b => <option key={b} value={b}>{b}</option>)}
                    </select>
                  )}
                  <select value={tagFilter} onChange={e => setTagFilter(e.target.value)} style={{ ...inputStyle, width: 'auto', marginBottom: 0, fontSize: 12, padding: '4px 8px' }}>
                    <option value="">All Tags</option>
                    {uniqueTags.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                  {(levelFilter || bandFilter || tagFilter) && (
                    <button onClick={clearFilters} style={{ ...btnSecondary, padding: '4px 8px', fontSize: 12 }}>Clear</button>
                  )}
                  {/* ✅ Enhanced count badge */}
                  <span style={{ 
                    fontSize: 12, 
                    color: (levelFilter || bandFilter || tagFilter) ? '#007BFF' : '#666',
                    fontWeight: (levelFilter || bandFilter || tagFilter) ? 600 : 400,
                    background: (levelFilter || bandFilter || tagFilter) ? '#e7f1ff' : 'transparent',
                    padding: '2px 6px',
                    borderRadius: 4,
                    marginLeft: 'auto'
                  }}>
                    Showing {filteredQuestions.length} of {rawQuestions.length}
                    {(levelFilter || bandFilter || tagFilter) && ' • Filters active'}
                  </span>
                </div>
              </div>

              {/* ✅ Increased maxHeight to show more questions */}
              <div style={{ maxHeight: '700px', overflowY: 'auto' }}>
                {filteredQuestions.length === 0 ? (
                  <p style={{ color: '#666', fontStyle: 'italic', textAlign: 'center', padding: 20 }}>No questions match your filters.</p>
                ) : (
                  filteredQuestions.map(q => (
                    <div key={q.id} style={{ padding: 12, borderBottom: '1px solid #eee', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
                      {renderQuestionPreview(q)}
                      <button onClick={() => handleAddNode(q)} style={{ ...btnAdd, width: 'auto', whiteSpace: 'nowrap', flexShrink: 0 }}>+ Add</button>
                    </div>
                  ))
                )}
              </div>
            </div>

            <div style={{ background: '#fff', padding: 15, borderRadius: 8, border: '1px solid #ddd' }}>
              <h3 style={{ marginTop: 0 }}>Exam Flow Nodes ({flowNodes.length})</h3>
              <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                {flowNodes.map((node, index) => (
                  <div key={node.tempId} style={{ padding: 10, marginBottom: 10, background: '#f8f9fa', borderRadius: 6, border: '1px solid #ddd' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                      <strong style={{ fontSize: 14 }}>Node {index + 1} (Q-ID: {node.source_question_id})</strong>
                      <button onClick={() => removeNode(node.tempId)} style={{ ...btnDanger, padding: '2px 6px', fontSize: 12 }}>✕</button>
                    </div>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, cursor: 'pointer' }}>
                      <input type="checkbox" checked={node.is_start} onChange={() => {
                        if (!node.is_start) {
                          setFlowNodes(prev => prev.map(n => ({ ...n, is_start: false })));
                          updateNode(node.tempId, 'is_start', true);
                        }
                      }} />
                      <strong>Start Question</strong>
                    </label>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                      <div>
                        <label style={{ fontSize: 12, fontWeight: 500 }}>If Correct →</label>
                        <select style={{ ...inputStyle, marginBottom: 0, fontSize: 12, padding: 5 }} value={node.next_correct_id ?? 'null'} onChange={e => updateNode(node.tempId, 'next_correct_id', e.target.value)}>
                          <option value="null">-- End --</option>
                          {flowNodes.map(n => n.tempId !== node.tempId && (<option key={n.tempId} value={n.tempId}>Node {flowNodes.indexOf(n) + 1}</option>))}
                        </select>
                      </div>
                      <div>
                        <label style={{ fontSize: 12, fontWeight: 500 }}>If Wrong →</label>
                        <select style={{ ...inputStyle, marginBottom: 0, fontSize: 12, padding: 5 }} value={node.next_wrong_id ?? 'null'} onChange={e => updateNode(node.tempId, 'next_wrong_id', e.target.value)}>
                          <option value="null">-- End --</option>
                          {flowNodes.map(n => n.tempId !== node.tempId && (<option key={n.tempId} value={n.tempId}>Node {flowNodes.indexOf(n) + 1}</option>))}
                        </select>
                      </div>
                    </div>
                  </div>
                ))}
                {flowNodes.length === 0 && <p style={{ color: '#666', fontStyle: 'italic' }}>No questions added yet.</p>}
              </div>
            </div>
          </div>
          <div style={{ marginTop: 20, display: 'flex', justifyContent: 'space-between' }}>
            <button onClick={() => setStep(1)} style={btnSecondary}>← Edit Details</button>
            <button onClick={handleSave} disabled={loading} style={{ ...btnPrimary, opacity: loading ? 0.6 : 1, fontSize: 16, padding: '12px 24px' }}>
              {loading ? (isEditMode ? 'Updating...' : 'Saving...') : (isEditMode ? '✅ Update Exam' : '✅ Save Exam')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}